import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, BarChart3, Database, Flag, Gauge, ListChecks, UserCheck, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { Avatar, PageHeader, Spinner } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Stat = ({ label, value, testid }) => (
  <div className="surface p-4" data-testid={testid}><p className="text-xs text-slate-500">{label}</p><p className="mt-1 font-serif text-3xl">{value}</p></div>
);

const Tile = ({ to, icon: Icon, title, text, count, testid }) => (
  <Link to={to} data-testid={testid} className="surface group flex items-start gap-4 p-5 transition-all hover:-translate-y-0.5 hover:shadow-md">
    <Icon size={20} className="mt-1 text-slate-600" />
    <div className="flex-1"><p className="font-serif text-xl">{title}</p><p className="text-sm text-slate-500">{text}</p></div>
    {count > 0 && <span className="rounded-full bg-[hsl(var(--social))] px-2.5 py-0.5 text-xs font-semibold text-white">{count}</span>}
    <ArrowRight size={16} className="mt-1.5 text-slate-400 transition-transform group-hover:translate-x-0.5" />
  </Link>
);

const Categories = () => {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState({ facts: "" });
  const load = () => api.get("/admin/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);
  const add = async (kind) => { try { await api.post("/admin/categories", { kind, name: name[kind] }); setName({ ...name, [kind]: "" }); load(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async (id) => { await api.delete(`/admin/categories/${id}`); load(); };
  return (
    <div className="grid gap-5 md:grid-cols-2">
      {["facts"].map((k) => (
        <div key={k} className="surface p-5" data-testid={`admin-categories-${k}`}>
          <p className="font-serif text-xl capitalize">{k} categories</p>
          <div className="mt-3 flex flex-wrap gap-1.5">{cats.filter((c) => c.kind === k).map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1 rounded-full bg-[#F5F1EB] py-1 pl-3 pr-1.5 text-xs">{c.name}<button data-testid={`delete-cat-${c.id}`} onClick={() => del(c.id)} className="rounded-full p-0.5 hover:bg-black/10" aria-label={`Remove ${c.name}`}><X size={11} /></button></span>
          ))}</div>
          <div className="mt-3 flex gap-2"><Input data-testid={`new-cat-${k}-input`} value={name[k]} onChange={(e) => setName({ ...name, [k]: e.target.value })} placeholder="New category" />
            <Button data-testid={`new-cat-${k}-btn`} disabled={!name[k].trim()} onClick={() => add(k)}>Add</Button></div>
        </div>
      ))}
    </div>
  );
};

const Users = () => {
  const { me } = useMe();
  const [data, setData] = useState(null);
  const load = () => api.get("/users").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const setRole = async (id, role) => { try { await api.patch(`/users/${id}/role`, { role }); toast.success("Role updated"); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!data) return <Spinner />;
  return (
    <div className="surface divide-y divide-border" data-testid="admin-users">
      {data.users.map((u) => (
        <div key={u.user_id} className="flex items-center gap-3 p-3.5">
          <Avatar user={u} size={32} />
          <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium">{u.name}</p><p className="truncate text-xs text-slate-500">{u.email || "sample member"}</p></div>
          {u.is_owner && <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white" data-testid={`owner-badge-${u.user_id}`}>{u.role === "admin" ? "Owner" : "Owner · admin on next sign-in"}</span>}
          <Select value={u.role} onValueChange={(v) => setRole(u.user_id, v)} disabled={u.user_id === me.user_id || u.is_owner}>
            <SelectTrigger className="w-32" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{data.roles.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
};

export default function AdminDashboard() {
  const [s, setS] = useState(null);
  const [pa, setPa] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); api.get("/admin/privacy/alerts").then((r) => setPa(r.data)).catch(() => {}); }, []);
  if (!s) return <Spinner />;
  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <PageHeader env="admin" title="Administration" subtitle="Approved sources, Facts verification, moderation, categories and roles." />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Members" value={s.users} testid="stat-users" /><Stat label="Posts" value={s.posts} testid="stat-posts" />
        <Stat label="Published facts" value={s.facts_published} testid="stat-facts" />
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Tile to="/admin/sources" icon={Database} title="Approved sources" text={`${s.sources_active} active of ${s.sources_total}`} testid="admin-tile-sources" />
        <Tile to="/admin/verification" icon={ListChecks} title="Facts to decide" text={s.facts_pending ? `${s.facts_pending} need your decision · the AI handles the rest` : "Nothing needs you · the AI is handling the news"} count={s.facts_pending} testid="admin-tile-verification" />
        <Tile to="/admin/moderation" icon={Flag} title="Moderation queue" text={`${s.reports_open} open reports`} count={s.reports_open} testid="admin-tile-moderation" />
        <Tile to="/admin/contributors" icon={Award} title="Contributors" text="Each member's verified Facts and confirmed corrections, plus every AI-applied correction (with revert)" testid="admin-tile-contributors" />
        <Tile to="/admin/access" icon={UserCheck} title="People & bot checks" text="Review accounts paused because they looked automated" testid="admin-tile-access" />
        <Tile to="/admin/analytics" icon={BarChart3} title="Analytics" text="Members, countries, AI spend & next month's forecast" testid="admin-tile-analytics" />
        <Tile to="/admin/fact-check" icon={Gauge} title="Fact-check & AI costs" text="Models, limits, thresholds, evidence sources, fact database" testid="admin-tile-factcheck" />
        <Tile to="/admin/live" icon={ListChecks} title="Middle Live & brand" text={s.live_suggestions ? `${s.live_suggestions} suggested issues to review · records, corrections, brand` : "Live records, suggestions, corrections, brand messages"} count={s.live_suggestions} testid="admin-tile-live" />
        <Tile to="/admin/discovery" icon={Database} title="Recommendations" text="Ranking weights & paid promotions" testid="admin-tile-discovery" />
        <Tile to="/admin/privacy" icon={Flag} title="Privacy" text={pa ? `${pa.counts.rights_open} open requests · ${pa.counts.breaches_open} open breaches` : "Requests, breaches, DPIAs, transfers"} count={pa?.attention} testid="admin-tile-privacy" />
      </div>
      <div className="space-y-3"><h2 className="text-2xl">Categories</h2><Categories /></div>
      <div className="space-y-3"><h2 className="text-2xl">Members & roles</h2><Users /></div>
    </div>
  );
}
